import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useSelector } from "react-redux";
import config from "../config.js";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Avatar,
  Tooltip,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  AccessTime,
 
  Delete,
  ArrowForward,
} from "@mui/icons-material";
import Layout from "../components/layout/Esign";

const EsignWorkplaceManagement = () => {
  const [workflows, setWorkflows] = useState({
    pending: [],
    inProgress: [],
    completed: [],
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  const userId = useSelector((state) => state.auth.userId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      document: "",
      assignee: "",
      deadline: "",
      priority: "Medium",
    },
  });

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/v1/Workflow`, {
          params: { userId },
        });

        if (response.data.success) {
          setWorkflows(response.data.data);
        } else {
          setError("Failed to fetch workflows");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch workflows");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, [userId, API_BASE_URL]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/v1/Workflow`, {
        ...data,
        userId,
        status: "pending",
      });

      if (response.data.success) {
        setWorkflows({
          ...workflows,
          pending: [...workflows.pending, response.data.data],
        });
        setOpenDialog(false);
        reset();
      } else {
        setError("Failed to create workflow");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create workflow");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const moveWorkflow = async (itemId, sourceStatus, targetStatus) => {
    try {
      setLoading(true);

      if (!itemId) {
        setError("Invalid workflow ID");
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/api/v1/workflow/${itemId}`,
        {
          status: targetStatus,
        }
      );

      if (response.data.success) {
        const sourceList = [...workflows[sourceStatus]];
        const item = sourceList.find((i) => i._id === itemId);
        if (!item) return;

        setWorkflows({
          ...workflows,
          [sourceStatus]: sourceList.filter((i) => i._id !== itemId),
          [targetStatus]: [
            ...workflows[targetStatus],
            { ...item, status: targetStatus },
          ],
        });
      } else {
        setError("Failed to move workflow");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to move workflow");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkflow = async (itemId, status) => {
    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_BASE_URL}/api/v1/workflow/${itemId}`
      );

      if (response.data.success) {
        setWorkflows({
          ...workflows,
          [status]: workflows[status].filter((i) => i._id !== itemId),
        });
      } else {
        setError("Failed to delete workflow");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete workflow");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusOrder = ["pending", "inProgress", "completed"];
    const currentIndex = statusOrder.indexOf(currentStatus);
    return currentIndex < statusOrder.length - 1
      ? statusOrder[currentIndex + 1]
      : null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Typography variant="h5">Workflow Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              disabled={loading}
            >
              New Workflow
            </Button>
          </Box>
        </motion.div>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Grid container spacing={3}>
            {Object.entries(workflows).map(([status, items]) => (
              <Grid item xs={12} md={4} key={status}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "#f5f5f5",
                    height: "100%",
                    minHeight: 400,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, textTransform: "capitalize" }}
                  >
                    {status.replace(/([A-Z])/g, " $1")}
                    <Chip
                      label={items.length}
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Typography>

                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <Stack spacing={2}>
                      <AnimatePresence>
                        {items.map((item) => (
                          <motion.div
                            key={item._id}
                            variants={itemVariants}
                            exit={{ opacity: 0, y: -20 }}
                            layout
                          >
                            <Card sx={{ "&:hover": { boxShadow: 3 } }}>
                              <CardContent>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {item.title}
                                  </Typography>
                                  <Chip
                                    label={item.priority}
                                    size="small"
                                    color={getPriorityColor(item.priority)}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {item.document}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 2,
                                    gap: 2,
                                  }}
                                >
                                  <Tooltip title={item.assignee}>
                                    <Avatar sx={{ width: 24, height: 24 }}>
                                      {item.assignee.charAt(0)}
                                    </Avatar>
                                  </Tooltip>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <AccessTime fontSize="small" />
                                    {new Date(
                                      item.deadline
                                    ).toLocaleDateString()}
                                  </Typography>
                                  <Box
                                    sx={{ ml: "auto", display: "flex", gap: 1 }}
                                  >
                                    {/* <IconButton size="small"
                                      onClick={() => {
                                        
                                      }}>
                                      <Edit fontSize="small" />
                                    </IconButton> */}
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        deleteWorkflow(item._id, status)
                                      }
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                    {getNextStatus(status) && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          moveWorkflow(
                                            item._id,
                                            status,
                                            getNextStatus(status)
                                          )
                                        }
                                        color="primary"
                                      >
                                        <ArrowForward fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Stack>
                  </motion.div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          reset();
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Create New Workflow</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Controller
                name="title"
                control={control}
                rules={{ required: "Title is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
              <Controller
                name="document"
                control={control}
                rules={{ required: "Document is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Document"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.document}
                    helperText={errors.document?.message}
                  />
                )}
              />
              <Controller
                name="assignee"
                control={control}
                rules={{ required: "Assignee is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Assignee"
                    fullWidth
                    error={!!errors.assignee}
                    helperText={errors.assignee?.message}
                  />
                )}
              />
              <Controller
                name="deadline"
                control={control}
                rules={{ required: "Deadline is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Deadline"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.deadline}
                    helperText={errors.deadline?.message}
                  />
                )}
              />
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select {...field} fullWidth label="Priority">
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDialog(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default EsignWorkplaceManagement;
